import { supabase } from "./client";
import { getCurrentEmpresaId } from "./auth-utils";
import { ErrorMessages } from "@/utils/errorMessages";

export type CustomerRow = {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  data_nascimento?: string | null;
  address?: string | null;
  cpf_cnpj?: string | null;
  inscricao_estadual?: string | null;
  ie_isento?: boolean | null;
};

export async function createCustomer(input: {
  name: string;
  phone?: string;
  email?: string;
  data_nascimento?: string | null;
  cpf_cnpj?: string;
  address?: string;
  tipo_cliente?: string;
  telefones?: string;
  contatos?: string | null;
  observacoes?: string | null;
  inscricao_municipal?: string | null;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_uf?: string;
  endereco_cep?: string;
  endereco_entrega_logradouro?: string | null;
  endereco_entrega_numero?: string | null;
  endereco_entrega_complemento?: string | null;
  endereco_entrega_bairro?: string | null;
  endereco_entrega_cidade?: string | null;
  endereco_entrega_uf?: string | null;
  endereco_entrega_cep?: string | null;
  endereco_cobranca_logradouro?: string | null;
  endereco_cobranca_numero?: string | null;
  endereco_cobranca_complemento?: string | null;
  endereco_cobranca_bairro?: string | null;
  endereco_cobranca_cidade?: string | null;
  endereco_cobranca_uf?: string | null;
  endereco_cobranca_cep?: string | null;
  inscricao_estadual?: string;
  ie_isento?: boolean;
}): Promise<{ ok: boolean; id?: string; data?: CustomerRow; error?: string }> {
  try {
    console.log("➕ Criando cliente no banco:", input);
    
    // Obter empresa_id do usuário logado usando a função existente
    let empresa_id: string;
    try {
      empresa_id = await getCurrentEmpresaId();
      if (!empresa_id) {
        console.error("❌ Erro ao obter empresa do usuário");
        return { ok: false, error: ErrorMessages.empresaNotFound() };
      }
    } catch (empresaError: any) {
      console.error("❌ Erro ao obter empresa_id:", empresaError);
      // Se já tem mensagem formatada, usar ela; senão, usar mensagem padrão
      const errorMessage = empresaError?.message?.includes('⏱️') 
        ? empresaError.message 
        : ErrorMessages.empresaNotFound();
      return { ok: false, error: errorMessage };
    }
    
    console.log("✅ Empresa encontrada:", empresa_id);
    
    // DEBUG: Log detalhado dos dados que serão enviados
    const dataToInsert = {
      empresa_id: empresa_id,
      name: input.name,
      phone: input.phone ?? null,
      email: input.email ?? null,
      data_nascimento: input.data_nascimento ?? null,
      cpf_cnpj: input.cpf_cnpj ?? null,
      address: input.address ?? null,
      tipo_cliente: input.tipo_cliente ?? "pessoa_fisica",
      telefones: input.telefones ?? null,
      contatos: input.contatos ?? null,
      observacoes: input.observacoes ?? null,
      inscricao_municipal: input.inscricao_municipal ?? null,
      endereco_logradouro: input.endereco_logradouro ?? null,
      endereco_numero: input.endereco_numero ?? null,
      endereco_complemento: input.endereco_complemento ?? null,
      endereco_bairro: input.endereco_bairro ?? null,
      endereco_cidade: input.endereco_cidade ?? null,
      endereco_uf: input.endereco_uf ?? null,
      endereco_cep: input.endereco_cep ?? null,
      endereco_entrega_logradouro: input.endereco_entrega_logradouro ?? null,
      endereco_entrega_numero: input.endereco_entrega_numero ?? null,
      endereco_entrega_complemento: input.endereco_entrega_complemento ?? null,
      endereco_entrega_bairro: input.endereco_entrega_bairro ?? null,
      endereco_entrega_cidade: input.endereco_entrega_cidade ?? null,
      endereco_entrega_uf: input.endereco_entrega_uf ?? null,
      endereco_entrega_cep: input.endereco_entrega_cep ?? null,
      endereco_cobranca_logradouro: input.endereco_cobranca_logradouro ?? null,
      endereco_cobranca_numero: input.endereco_cobranca_numero ?? null,
      endereco_cobranca_complemento: input.endereco_cobranca_complemento ?? null,
      endereco_cobranca_bairro: input.endereco_cobranca_bairro ?? null,
      endereco_cobranca_cidade: input.endereco_cobranca_cidade ?? null,
      endereco_cobranca_uf: input.endereco_cobranca_uf ?? null,
      endereco_cobranca_cep: input.endereco_cobranca_cep ?? null,
      inscricao_estadual: input.inscricao_estadual ?? null,
      ie_isento: input.ie_isento ?? false,
    };
    
    console.log("🔧 DEBUG createCustomer: Dados que serão inseridos:", JSON.stringify(dataToInsert, null, 2));
    
    const { data, error } = await supabase
      .from("customers")
      .insert(dataToInsert)
      .select("*")
      .single();
    
    if (error) {
      console.error("❌ Erro ao criar cliente - DETALHES COMPLETOS:");
      console.error("   Código do erro:", error.code);
      console.error("   Mensagem:", error.message);
      console.error("   Detalhes:", error.details);
      console.error("   Hint:", error.hint);
      console.error("   Dados enviados:", dataToInsert);
      
      // Tratar erros de constraint única de forma mais amigável
      
      // Tratar erros de constraint única de forma mais amigável
      if (error.code === '23505') { // Violation of unique constraint
        if (error.message?.includes('customers_email_key') || error.message?.includes('email')) {
          return { ok: false, error: 'Este email já está cadastrado. Por favor, verifique se o cliente já existe ou use um email diferente.' };
        }
        if (error.message?.includes('customers_phone_key') || error.message?.includes('phone')) {
          return { ok: false, error: 'Este telefone já está cadastrado. Por favor, verifique se o cliente já existe ou use um telefone diferente.' };
        }
        if (error.message?.includes('duplicate key')) {
          return { ok: false, error: 'Já existe um cliente com estes dados. Por favor, verifique os dados informados.' };
        }
      }
      
      // Melhorar mensagem de erro para RLS
      if (error.message?.includes('row-level security') || error.message?.includes('RLS')) {
        return { ok: false, error: ErrorMessages.permissionDenied() };
      }
      
      // Se for erro de fornecedores (pode ser confusão de página)
      if (error.message?.includes('fornecedores')) {
        return { ok: false, error: 'Erro ao cadastrar. Verifique se você está na página correta (Clientes, não Fornecedores).' };
      }
      
      throw new Error(ErrorMessages.saveError("o cliente"));
    }
    
    console.log("✅ Cliente criado com sucesso:", data.id);
    return { ok: true, id: data?.id, data: data };
  } catch (e: unknown) {
    console.error("❌ Erro na função createCustomer:", e);
    // Se já tem mensagem formatada, usar ela; senão, usar mensagem padrão
    const errorMessage = (e as any)?.message?.includes('⏱️') 
      ? (e as any).message 
      : ErrorMessages.saveError("o cliente");
    return { ok: false, error: errorMessage };
  }
}

export async function updateCustomer(id: string, input: { name?: string; phone?: string; email?: string; address?: string, cpf_cnpj?: string, inscricao_estadual?: string, ie_isento?: boolean, tipo_cliente?: string, telefones?: string, contatos?: string | null, observacoes?: string | null, inscricao_municipal?: string | null, endereco_logradouro?: string, endereco_numero?: string, endereco_complemento?: string, endereco_bairro?: string, endereco_cidade?: string, endereco_uf?: string, endereco_cep?: string, endereco_entrega_logradouro?: string | null, endereco_entrega_numero?: string | null, endereco_entrega_complemento?: string | null, endereco_entrega_bairro?: string | null, endereco_entrega_cidade?: string | null, endereco_entrega_uf?: string | null, endereco_entrega_cep?: string | null, endereco_cobranca_logradouro?: string | null, endereco_cobranca_numero?: string | null, endereco_cobranca_complemento?: string | null, endereco_cobranca_bairro?: string | null, endereco_cobranca_cidade?: string | null, endereco_cobranca_uf?: string | null, endereco_cobranca_cep?: string | null }): Promise<{ ok: boolean; data?: CustomerRow; error?: string }> {
  try {
    console.log("🔍 Atualizando cliente:", { id, input });
    
    // Primeiro, verificar se o cliente existe
    const { data: existingCustomer, error: fetchError } = await supabase
      .from("customers")
      .select("id, name")
      .eq("id", id)
      .single();
    
    if (fetchError) {
      console.error("❌ Erro ao buscar cliente:", fetchError);
      return { ok: false, error: "Cliente não encontrado" };
    }
    
    if (!existingCustomer) {
      console.error("❌ Cliente não encontrado com ID:", id);
      return { ok: false, error: "Cliente não encontrado" };
    }
    
    console.log("✅ Cliente encontrado:", existingCustomer.name);
    
    const updateData: {
      name?: string;
      phone?: string;
      email?: string;
      address?: string | null;
      cpf_cnpj?: string | null;
      inscricao_estadual?: string | null;
      ie_isento?: boolean | null;
      tipo_cliente?: string;
      telefones?: string;
      contatos?: string | null;
      observacoes?: string | null;
      inscricao_municipal?: string | null;
      endereco_logradouro?: string | null;
      endereco_numero?: string | null;
      endereco_complemento?: string | null;
      endereco_bairro?: string | null;
      endereco_cidade?: string | null;
      endereco_uf?: string | null;
      endereco_cep?: string | null;
      endereco_entrega_logradouro?: string | null;
      endereco_entrega_numero?: string | null;
      endereco_entrega_complemento?: string | null;
      endereco_entrega_bairro?: string | null;
      endereco_entrega_cidade?: string | null;
      endereco_entrega_uf?: string | null;
      endereco_entrega_cep?: string | null;
      endereco_cobranca_logradouro?: string | null;
      endereco_cobranca_numero?: string | null;
      endereco_cobranca_complemento?: string | null;
      endereco_cobranca_bairro?: string | null;
      endereco_cobranca_cidade?: string | null;
      endereco_cobranca_uf?: string | null;
      endereco_cobranca_cep?: string | null;
    } = {};
    if (input.name !== undefined) updateData.name = input.name;
    if (input.phone !== undefined) updateData.phone = input.phone;
    if (input.email !== undefined) updateData.email = input.email;
    if (input.address !== undefined) updateData.address = input.address;
    if (input.cpf_cnpj !== undefined) updateData.cpf_cnpj = input.cpf_cnpj;
    if (input.inscricao_estadual !== undefined) updateData.inscricao_estadual = input.inscricao_estadual;
    if (input.ie_isento !== undefined) updateData.ie_isento = input.ie_isento;
    if (input.tipo_cliente !== undefined) updateData.tipo_cliente = input.tipo_cliente;
    if (input.telefones !== undefined) updateData.telefones = input.telefones;
    if (input.contatos !== undefined) updateData.contatos = input.contatos;
    if (input.observacoes !== undefined) updateData.observacoes = input.observacoes;
    if (input.inscricao_municipal !== undefined) updateData.inscricao_municipal = input.inscricao_municipal;
    if (input.endereco_logradouro !== undefined) updateData.endereco_logradouro = input.endereco_logradouro;
    if (input.endereco_numero !== undefined) updateData.endereco_numero = input.endereco_numero;
    if (input.endereco_complemento !== undefined) updateData.endereco_complemento = input.endereco_complemento;
    if (input.endereco_bairro !== undefined) updateData.endereco_bairro = input.endereco_bairro;
    if (input.endereco_cidade !== undefined) updateData.endereco_cidade = input.endereco_cidade;
    if (input.endereco_uf !== undefined) updateData.endereco_uf = input.endereco_uf;
    if (input.endereco_cep !== undefined) updateData.endereco_cep = input.endereco_cep;
    if (input.endereco_entrega_logradouro !== undefined) updateData.endereco_entrega_logradouro = input.endereco_entrega_logradouro;
    if (input.endereco_entrega_numero !== undefined) updateData.endereco_entrega_numero = input.endereco_entrega_numero;
    if (input.endereco_entrega_complemento !== undefined) updateData.endereco_entrega_complemento = input.endereco_entrega_complemento;
    if (input.endereco_entrega_bairro !== undefined) updateData.endereco_entrega_bairro = input.endereco_entrega_bairro;
    if (input.endereco_entrega_cidade !== undefined) updateData.endereco_entrega_cidade = input.endereco_entrega_cidade;
    if (input.endereco_entrega_uf !== undefined) updateData.endereco_entrega_uf = input.endereco_entrega_uf;
    if (input.endereco_entrega_cep !== undefined) updateData.endereco_entrega_cep = input.endereco_entrega_cep;
    if (input.endereco_cobranca_logradouro !== undefined) updateData.endereco_cobranca_logradouro = input.endereco_cobranca_logradouro;
    if (input.endereco_cobranca_numero !== undefined) updateData.endereco_cobranca_numero = input.endereco_cobranca_numero;
    if (input.endereco_cobranca_complemento !== undefined) updateData.endereco_cobranca_complemento = input.endereco_cobranca_complemento;
    if (input.endereco_cobranca_bairro !== undefined) updateData.endereco_cobranca_bairro = input.endereco_cobranca_bairro;
    if (input.endereco_cobranca_cidade !== undefined) updateData.endereco_cobranca_cidade = input.endereco_cobranca_cidade;
    if (input.endereco_cobranca_uf !== undefined) updateData.endereco_cobranca_uf = input.endereco_cobranca_uf;
    if (input.endereco_cobranca_cep !== undefined) updateData.endereco_cobranca_cep = input.endereco_cobranca_cep;
    
    console.log("📝 Dados para atualização:", updateData);
    
    const { data, error } = await supabase
      .from("customers")
      .update(updateData)
      .eq("id", id)
      .select("*");
    
    if (error) {
      console.error("❌ Erro do Supabase na atualização:", error);
      
      // Se o erro for sobre a coluna address não existir, tentar novamente sem ela
      if (error.message?.includes("address") && error.message?.includes("schema cache")) {
        console.warn("⚠️ Coluna address não encontrada, tentando atualizar sem esse campo");
        const updateDataWithoutAddress: {
          name?: string;
          phone?: string;
          email?: string;
        } = {};
        
        if (input.name !== undefined) updateDataWithoutAddress.name = input.name;
        if (input.phone !== undefined) updateDataWithoutAddress.phone = input.phone;
        if (input.email !== undefined) updateDataWithoutAddress.email = input.email;
        
        const { data: retryData, error: retryError } = await supabase
          .from("customers")
          .update(updateDataWithoutAddress)
          .eq("id", id)
          .select("*");
        
        if (retryError) {
          console.error("❌ Erro ao atualizar cliente (sem address):", retryError);
          if (retryError.message?.includes('row-level security') || retryError.message?.includes('RLS')) {
            return { ok: false, error: "Sem permissão para atualizar este cliente" };
          }
          return { ok: false, error: retryError.message || "Erro ao atualizar cliente" };
        }
        
        console.log("✅ Cliente atualizado com sucesso (sem address):", retryData[0]);
        return { ok: true, data: retryData[0] as CustomerRow };
      }
      
      // Melhorar mensagem de erro
      if (error.message?.includes('row-level security') || error.message?.includes('RLS')) {
        return { ok: false, error: "Sem permissão para atualizar este cliente" };
      }
      // Se o erro for sobre updated_at não existir, tentar novamente sem o trigger
      if (error.message?.includes('updated_at') && error.message?.includes('has no field')) {
        console.warn("⚠️ Coluna updated_at não encontrada, tentando atualizar novamente");
        // O erro pode ser do trigger, mas vamos tentar novamente
        // Se persistir, o usuário precisa executar o script SQL para adicionar a coluna
        return { 
          ok: false, 
          error: "Erro: a coluna 'updated_at' não existe na tabela. Execute o script SQL 'adicionar-coluna-updated_at-customers.sql' no Supabase." 
        };
      }
      
      if (error.message?.includes('updated_at')) {
        // Se o erro for sobre updated_at, pode ser que o trigger não esteja funcionando
        // Mas não devemos falhar por isso, apenas logar
        console.warn("⚠️ Aviso sobre updated_at (pode ser ignorado se o trigger estiver configurado):", error.message);
      }
      return { ok: false, error: error.message || "Erro ao atualizar cliente" };
    }
    
    if (!data || data.length === 0) {
      console.error("❌ Nenhum cliente retornado após atualização");
      // Tentar buscar o cliente novamente para verificar se foi atualizado
      const { data: updatedClient, error: fetchError } = await supabase
        .from("customers")
        .select("*")
        .eq("id", id)
        .single();
      
      if (fetchError || !updatedClient) {
        console.error("❌ Cliente não encontrado após atualização");
        return { ok: false, error: "Erro ao atualizar cliente" };
      }
      
      console.log("✅ Cliente atualizado (verificação posterior):", updatedClient);
      return { ok: true, data: updatedClient as CustomerRow };
    }
    
    console.log("✅ Cliente atualizado com sucesso:", data[0]);
    return { ok: true, data: data[0] as CustomerRow };
  } catch (e: unknown) {
    console.error("❌ Erro na função updateCustomer:", e);
    const errorMessage = (e as any)?.message || "Erro ao atualizar cliente";
    // Filtrar mensagens de erro estranhas que podem vir de traduções incorretas
    if (errorMessage.includes('disco') || errorMessage.includes('Novo')) {
      console.warn("⚠️ Mensagem de erro estranha detectada, usando mensagem genérica");
      return { ok: false, error: "Erro ao atualizar cliente. Verifique se todos os campos estão corretos." };
    }
    return { ok: false, error: errorMessage };
  }
}

export async function deleteCustomer(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from("customers")
      .delete()
      .eq("id", id);
    if (error) throw error;
    return { ok: true };
  } catch (e: unknown) {
    return { ok: false, error: e?.message ?? "Erro ao excluir cliente" };
  }
}

export async function deleteInvalidCustomers(): Promise<{ ok: boolean; deletedCount?: number; error?: string }> {
  try {
    console.log("🧹 Limpando clientes inválidos do banco...");
    
    const empresa_id = await getCurrentEmpresaId();
    if (!empresa_id) {
      return { ok: false, error: "Erro ao obter empresa do usuário" };
    }
    
    // Buscar todos os clientes da empresa
    const { data: customers, error: fetchError } = await supabase
      .from("customers")
      .select("id, name")
      .eq("empresa_id", empresa_id);
    
    if (fetchError) {
      console.error("❌ Erro ao buscar clientes:", fetchError);
      return { ok: false, error: "Erro ao buscar clientes" };
    }
    
    if (!customers || customers.length === 0) {
      return { ok: true, deletedCount: 0 };
    }
    
    // Identificar clientes inválidos (nomes muito curtos ou códigos de país)
    const countryCodes = ['BR', 'AO', 'AR', 'BD', 'CO', 'CR', 'IN', 'MX', 'MZ', 'NI', 'PK', 'PT', 'TM'];
    const invalidCustomers = customers.filter(customer => {
      const name = customer.name?.trim() || "";
      return name.length <= 3 || countryCodes.includes(name.toUpperCase());
    });
    
    if (invalidCustomers.length === 0) {
      console.log("✅ Nenhum cliente inválido encontrado");
      return { ok: true, deletedCount: 0 };
    }
    
    console.log(`🗑️ Encontrados ${invalidCustomers.length} clientes inválidos para exclusão`);
    
    // Excluir clientes inválidos em lotes (Supabase tem limite de 1000 por vez)
    const batchSize = 100;
    let deletedCount = 0;
    
    for (let i = 0; i < invalidCustomers.length; i += batchSize) {
      const batch = invalidCustomers.slice(i, i + batchSize);
      const ids = batch.map(c => c.id);
      
      const { error: deleteError } = await supabase
        .from("customers")
        .delete()
        .in("id", ids);
      
      if (deleteError) {
        console.error("❌ Erro ao excluir lote de clientes:", deleteError);
        return { ok: false, error: `Erro ao excluir clientes: ${deleteError.message}` };
      }
      
      deletedCount += batch.length;
      console.log(`✅ Lote ${Math.floor(i / batchSize) + 1} excluído: ${batch.length} clientes`);
    }
    
    console.log(`✅ Limpeza concluída: ${deletedCount} clientes inválidos excluídos`);
    return { ok: true, deletedCount };
  } catch (e: unknown) {
    console.error("❌ Erro ao limpar clientes inválidos:", e);
    return { ok: false, error: (e as any)?.message ?? "Erro ao limpar clientes inválidos" };
  }
}

export async function getCustomers(): Promise<CustomerRow[]> {
  try {
    console.log("🔍 Buscando clientes...");

    // Obter empresa_id do usuário logado
    const empresa_id = await getCurrentEmpresaId();

    if (!empresa_id) {
      console.error("❌ Erro ao obter empresa do usuário");
      return [];
    }

    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("empresa_id", empresa_id)
      .order("name", { ascending: true });

    if (error) {
      console.error("❌ Erro ao buscar clientes:", error);
      throw error;
    }

    console.log("✅ Clientes encontrados:", data?.length || 0);
    return data || [];
  } catch (error) {
    console.error("❌ Erro ao buscar clientes:", error);
    throw error;
  }
}

/**
 * Sincronizar clientes de pedidos antigos que não foram criados na tabela customers
 * ⚠️ PROTEÇÕES CONTRA DUPLICADOS:
 * - Roda apenas 1 vez por empresa (flag na tabela empresas)
 * - Normaliza nomes (remove espaços extras, acentos)
 * - Comparação em batch para evitar race conditions
 */
export async function syncCustomersFromOrders(): Promise<{ ok: boolean; created?: number; alreadySynced?: boolean; error?: string }> {
  try {
    console.log("🔄 [syncCustomersFromOrders] Sincronizando clientes de pedidos...");

    const empresa_id = await getCurrentEmpresaId();
    if (!empresa_id) {
      return { ok: false, error: "Empresa não encontrada" };
    }

    // 🔒 PROTEÇÃO 1: Verificar se já foi sincronizado
    const { data: empresa } = await supabase
      .from("empresas")
      .select("customers_synced")
      .eq("id", empresa_id)
      .single();

    if (empresa?.customers_synced) {
      console.log("⚠️ Sincronização já foi executada para esta empresa");
      return { ok: true, alreadySynced: true, created: 0 };
    }

    // 1. Buscar todos os pedidos da empresa
    const { data: orders, error: ordersError } = await supabase
      .from("atelie_orders")
      .select("customer_name, customer_phone, customer_email")
      .eq("empresa_id", empresa_id);

    if (ordersError) {
      return { ok: false, error: ordersError.message };
    }

    if (!orders || orders.length === 0) {
      console.log("✅ Nenhum pedido encontrado");
      // Marcar como sincronizado mesmo sem pedidos
      await supabase
        .from("empresas")
        .update({ customers_synced: true })
        .eq("id", empresa_id);
      return { ok: true, created: 0 };
    }

    // 2️⃣ PROTEÇÃO 2: Normalizar nomes para evitar duplicados
    const normalizeString = (str: string) =>
      str.toLowerCase().trim().replace(/\s+/g, ' ').normalize('NFD').replace(/[̀-ͯ]/g, '');

    // 3. Extrair clientes únicos (por nome normalizado)
    const uniqueCustomers = new Map<string, any>();
    orders.forEach((order: any) => {
      if (order.customer_name) {
        const normalizedKey = normalizeString(order.customer_name);
        // Se a chave normalizada não existe OU o nome original é melhor, atualizar
        if (!uniqueCustomers.has(normalizedKey) || order.customer_phone) {
          uniqueCustomers.set(normalizedKey, {
            name: order.customer_name.trim(),
            phone: order.customer_phone || null,
            email: order.customer_email || null,
            normalized: normalizedKey,
          });
        }
      }
    });

    console.log(`📊 Encontrados ${uniqueCustomers.size} clientes únicos em pedidos`);

    // 4️⃣ PROTEÇÃO 3: Buscar todos os clientes existentes (para comparação em batch)
    const { data: existingCustomers } = await supabase
      .from("customers")
      .select("id, name")
      .eq("empresa_id", empresa_id);

    const existingNormalized = new Set<string>();
    existingCustomers?.forEach((c: any) => {
      existingNormalized.add(normalizeString(c.name));
    });

    console.log(`👥 Existem ${existingCustomers?.length || 0} clientes já cadastrados`);

    // 5. Criar apenas clientes que não existem (com nome normalizado diferente)
    let created = 0;
    for (const customer of uniqueCustomers.values()) {
      if (!existingNormalized.has(customer.normalized)) {
        const { error: insertError } = await supabase
          .from("customers")
          .insert({
            empresa_id,
            name: customer.name,
            phone: customer.phone,
            email: customer.email,
          });

        if (!insertError) {
          created++;
          console.log(`✅ Cliente criado: ${customer.name}`);
          existingNormalized.add(customer.normalized); // Evitar duplicados na mesma sincronização
        } else {
          console.warn(`⚠️ Erro ao criar cliente ${customer.name}:`, insertError.message);
        }
      } else {
        console.log(`⏭️ Cliente já existe: ${customer.name}`);
      }
    }

    // 🔒 PROTEÇÃO 4: Marcar como sincronizado apenas se sucesso completo
    await supabase
      .from("empresas")
      .update({ customers_synced: true })
      .eq("id", empresa_id);

    console.log(`🎉 Sincronização concluída: ${created} clientes criados`);
    return { ok: true, created };
  } catch (error: any) {
    console.error("❌ Erro ao sincronizar clientes:", error);
    return { ok: false, error: error.message };
  }
}


